import saveIconWhite from "@assets/images/save-icon-white.svg";
import xWhite from "@assets/images/x-white.svg";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  SearchMaterialBy,
  useSearchSuggestionMaterialLazyQuery
} from "@saleor/graphql";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";

interface DialogEditMaterial {
  onConfirm: (data: any) => void;
  onClose: () => void;
  open: boolean;
  data: any;
  errorsEdit?: {
    materialCode?: boolean;
    soldToMaterialCode?: boolean;
    materialCodeValue?: string;
    soldToMaterialCodeValue?: string;
  } | null;
  onClearErrors?: (
    field:
      | "soldToMaterialCode"
      | "materialCode"
      | "soldToMaterialCodeValue"
      | "materialCodeValue"
  ) => void;
}

const DialogEditMaterial: React.FC<DialogEditMaterial> = ({
  onConfirm,
  onClose,
  open,
  data,
  errorsEdit = null,
  onClearErrors
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [isDisabledBtn, setIsDisabledBtn] = useState(true);
  const [initialMatCodeValue, setInitialMatCodeValue] = useState("");
  const skipReinitializeMaterialCode = useRef(false);

  const checkDataChanged = (
    soldToMaterialCode: string,
    materialCode: string
  ) => {
    const isSoldToChanged =
      soldToMaterialCode !== (data?.soldToMaterialCode || "");
    const isMaterialCodeChanged = materialCode !== (data?.materialCode || "");
    return isSoldToChanged || isMaterialCodeChanged;
  };
  const [isForceUpdateDefault, setIsForceUpdateDefault] = useState(false);
  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionMaterialLazyQuery({ fetchPolicy: "no-cache" });

  const textError = intl.formatMessage({
    defaultMessage: "โปรดระบุข้อมูล"
  });

  const duplicateErrorCus = intl.formatMessage({
    defaultMessage: "Duplicate Customer Material"
  });

  const duplicateErrorMat = intl.formatMessage({
    defaultMessage: "Duplicate Material Code"
  });

  const MaterialFilterSchema = useMemo(
    () =>
      Yup.object().shape({
        soldToMaterialCode: Yup.string()
          .required(textError)
          .test("no-duplicate", duplicateErrorCus, function(_) {
            if (errorsEdit?.soldToMaterialCode) {
              return false;
            }
            return true;
          }),
        materialCode: Yup.string()
          .required(textError)
          .test("no-duplicate", duplicateErrorMat, function(value) {
            if (
              errorsEdit?.materialCode &&
              value === errorsEdit?.materialCodeValue
            ) {
              return false;
            }
            return true;
          })
      }),
    [textError, duplicateErrorCus, duplicateErrorMat, errorsEdit, data]
  );

  const formik = useFormik({
    initialValues: {
      soldToMaterialCode: "",
      materialCode: ""
    },
    validationSchema: MaterialFilterSchema,
    enableReinitialize: true,
    onSubmit: () => {
      //
    }
  });

  const onSubmitForm = async () => {
    await formik.validateForm();
    formik.setTouched({
      soldToMaterialCode: true,
      materialCode: true
    });
    if (formik.isValid) {
      onConfirm(formik.values);
    }
  };

  useEffect(() => {
    if (data) {
      formik.resetForm();

      const soldToMaterialCodeValue = errorsEdit?.soldToMaterialCodeValue
        ? errorsEdit?.soldToMaterialCodeValue
        : formik.values.soldToMaterialCode
        ? formik.values.soldToMaterialCode
        : data.soldToMaterialCode || "";
      const materialCodeValue = formik.values.materialCode || "";

      formik.setValues({
        soldToMaterialCode: soldToMaterialCodeValue,
        materialCode: materialCodeValue
      });

      if (!errorsEdit?.materialCode && !skipReinitializeMaterialCode.current) {
        setInitialMatCodeValue(data.materialCode || "");
        skipReinitializeMaterialCode.current = false;
      }

      const codeToSearch = errorsEdit?.materialCodeValue || data.materialCode;
      if (codeToSearch) {
        handleChangeMatCodeKeyword(codeToSearch);
      }
    }
  }, [data, errorsEdit?.materialCode, errorsEdit?.materialCodeValue]);

  useEffect(() => {
    if (errorsEdit?.soldToMaterialCodeValue) {
      formik.setFieldValue(
        "soldToMaterialCode",
        errorsEdit.soldToMaterialCodeValue
      );
    } else {
      formik.setFieldValue(
        "soldToMaterialCode",
        formik.values.soldToMaterialCode
          ? formik.values.soldToMaterialCode
          : data.soldToMaterialCode || ""
      );
    }
  }, [errorsEdit?.soldToMaterialCodeValue]);

  useEffect(() => {
    const hasChanged = checkDataChanged(
      formik.values.soldToMaterialCode,
      formik.values.materialCode
    );
    setIsDisabledBtn(!hasChanged);
  }, [formik.values.soldToMaterialCode, formik.values.materialCode, data]);

  const handleChangeMatCodeKeyword = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSuggestions({
        variables: {
          first: 100,
          filter: { matCode: value },
          saleOrg: data?.salesOrganization?.code || "",
          distributionChannel: data?.distributionChannelCode || "",
          searchBy: SearchMaterialBy.MAT_CODE,
          status: "active"
        }
      });
    }
  };

  const dataMatCode = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSuggestions?.searchSuggestionMaterial) || [];
    return data.map(data => ({
      title: `${data.displayText}`,
      value: data.materialCode
    }));
  }, [fetchDataSuggestions?.searchSuggestionMaterial]);

  useEffect(() => {
    if (
      data.materialCode &&
      dataMatCode.length > 0 &&
      initialMatCodeValue === data.materialCode
    ) {
      const matchingOption = dataMatCode.find(
        x => x.value === data.materialCode
      );
      if (matchingOption) {
        setInitialMatCodeValue(matchingOption.title);
        formik.setFieldValue("materialCode", matchingOption.value || "");
      }
    }

    if (
      (errorsEdit?.materialCode || errorsEdit?.soldToMaterialCode) &&
      errorsEdit?.materialCodeValue
    ) {
      const matchingOption = dataMatCode.find(
        x => x.value === errorsEdit.materialCodeValue
      );

      if (!initialMatCodeValue && !errorsEdit?.materialCode) {
        setInitialMatCodeValue("");
        formik.setFieldValue("materialCode", "");
      } else if (matchingOption) {
        setInitialMatCodeValue(matchingOption.title);
        formik.setFieldValue("materialCode", matchingOption.value || "");
      }
    }
  }, [
    dataMatCode,
    data.materialCode,
    initialMatCodeValue,
    errorsEdit?.materialCode
  ]);

  const getValueFromOptionMatCode = title =>
    dataMatCode.find(x => x.title === title)?.value;

  const handleChangeMatCode = (value: string) => {
    if (!value) {
      if (formik.values.materialCode && formik.values.materialCode !== "") {
        formik.setFieldValue("materialCode", "");
        setInitialMatCodeValue("");

        if (errorsEdit?.materialCode && onClearErrors) {
          onClearErrors("materialCode");
          onClearErrors("materialCodeValue");
          setIsForceUpdateDefault(true);
        }
      } else if (errorsEdit?.materialCode && errorsEdit?.materialCodeValue) {
        formik.setFieldValue("materialCode", errorsEdit.materialCodeValue);
      } else {
        if (isForceUpdateDefault) {
          formik.setFieldValue("materialCode", "");
          setInitialMatCodeValue("");
          setIsForceUpdateDefault(false);
        } else {
          if (errorsEdit) {
            setInitialMatCodeValue(errorsEdit.materialCodeValue);
            formik.setFieldValue(
              "materialCode",
              errorsEdit.materialCodeValue || ""
            );
            handleChangeMatCodeKeyword(errorsEdit.materialCodeValue);
          } else if (data?.sapMaterial?.materialCode === data?.materialCode) {
            setInitialMatCodeValue(
              `${data?.sapMaterial?.displayText || data.materialCode}`
            );
            formik.setFieldValue("materialCode", data.materialCode || "");
          }
        }

        if (errorsEdit?.materialCode && onClearErrors) {
          onClearErrors("materialCode");
          onClearErrors("materialCodeValue");
          setIsForceUpdateDefault(true);
        }
      }
    } else {
      const selectedValue = getValueFromOptionMatCode(value);
      if (selectedValue) {
        formik.setFieldValue("materialCode", selectedValue);
      }
    }
  };

  return (
    <Dialog open={open} className={`${classes.root}`} fullWidth maxWidth="md">
      <DialogTitle className={classes.dialogTitle}>
        <MyTypography variant="h6" className={classes.titleHeader}>
          <span>
            <strong>Edit</strong> Customer Material
          </span>
          <InlineSVG
            data-testid="close-icon"
            src={xWhite}
            width={24}
            height={24}
            onClick={onClose}
            className={classes.closeIcon}
          />
        </MyTypography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} className={classes.detailsContainer}>
          <Grid item xs={4}>
            <p>Sold-to Party</p>
          </Grid>
          <Grid item xs={8}>
            <p>{data?.soldTo?.displayText || ""}</p>
          </Grid>
          <Grid item xs={4}>
            <p>Sales Org.</p>
          </Grid>
          <Grid item xs={8}>
            <p>{data?.salesOrganization?.displayText || ""}</p>
          </Grid>
          <Grid item xs={4}>
            <p>Distribution Channel</p>
          </Grid>
          <Grid item xs={8}>
            <p>{data?.distributionChannelCode || ""}</p>
          </Grid>
          <Grid item xs={5}>
            <div className={classes.inputBlock}>
              <MyTypography variant={"subtitle3"}>
                Customer Material
                <span className={classes.required}>*</span>
              </MyTypography>
              <SingleTextField
                name={"soldToMaterialCode"}
                value={formik.values.soldToMaterialCode}
                showClearIcon={true}
                className={classes.inputTextField}
                maxLengthProps={35}
                onChange={event => {
                  const newValue = event.target.value.trim();
                  formik.setFieldValue("soldToMaterialCode", newValue);
                  if (errorsEdit?.soldToMaterialCode && onClearErrors) {
                    onClearErrors("soldToMaterialCode");
                    onClearErrors("soldToMaterialCodeValue");
                  }
                  if (errorsEdit?.materialCode && onClearErrors) {
                    skipReinitializeMaterialCode.current = true;
                    onClearErrors("materialCode");
                    onClearErrors("materialCodeValue");
                  }
                }}
                placeholder={"ระบุ Customer Material"}
                error={
                  (formik.touched.soldToMaterialCode ||
                    errorsEdit?.soldToMaterialCode) &&
                  !!formik.errors.soldToMaterialCode
                }
              />
              {(formik.touched.soldToMaterialCode ||
                errorsEdit?.soldToMaterialCode) &&
                formik.errors.soldToMaterialCode && (
                  <MyTypography variant="caption" color="error">
                    {formik.errors.soldToMaterialCode}
                  </MyTypography>
                )}
            </div>
          </Grid>
          <Grid item xs={7}>
            <div className={classes.inputBlock}>
              <MyTypography variant={"subtitle3"}>
                Material Code / Description
                <span className={classes.required}>*</span>
              </MyTypography>
              <Autocomplete
                resetAlways
                optionsProp={dataMatCode}
                placeholderProps={intl.formatMessage({
                  defaultMessage: "ค้นหา Material Code / Description",
                  description: "label"
                })}
                onChangeProps={handleChangeMatCodeKeyword}
                onSearch={handleChangeMatCode}
                isReset={false}
                loading={loadingDataSuggestion}
                id="materialCodeDescription"
                name="materialCodeDescription"
                testId="materialCodeDescription"
                initialValue={initialMatCodeValue}
                error={
                  (formik.touched.materialCode || errorsEdit?.materialCode) &&
                  !!formik.errors.materialCode
                }
              />
              {(formik.touched.materialCode || errorsEdit?.materialCode) &&
                formik.errors.materialCode && (
                  <MyTypography variant="caption" color="error">
                    {formik.errors.materialCode}
                  </MyTypography>
                )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions disableSpacing={true}>
        <Button
          color="primary"
          variant="outlined"
          className={classes.typeButton}
          onClick={onClose}
        >
          <MyTypography variant="body1" className={classes.btnClose}>
            ปิดหน้าต่าง
          </MyTypography>
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={isDisabledBtn}
          className={`${classes.typeButton} ${classes.typeButtonSubmit}`}
          onClick={onSubmitForm}
        >
          <InlineSVG
            data-testid="save-icon"
            src={saveIconWhite}
            width={16}
            height={16}
            className={classes.closeIcon}
            style={{ marginRight: 8 }}
          />
          <MyTypography variant="body1" className={classes.btnSubmit}>
            บันทึกข้อมูล
          </MyTypography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogEditMaterial;
